var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@application/store/useStore";
import { updateAccessPointGeneral } from "@reducers/access-points/thunk/access-point-thunk";
import { toast } from "@ui/utils/toaster";
import { getLatestApActivityIso } from "@utility/access-point-monitoring";
export const useAPForm = ({ isEdit, ap }) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation(["form", "validation", "common"]);
    const { updateGeneralLoading, updateGeneralError, updateConfigLoading } = useAppSelector((state) => state.accessPoint);
    // Form setup with default values
    const methods = useForm({
        defaultValues: {
            accessPointUID: "",
            accessPointName: "",
            lastUpdated: "",
            calibrationDate: "",
            installationDate: "",
            toolTipText: "",
            isActive: true,
            address: "",
            place: "",
            administratorName: "",
            administratorPhone: "",
            administrators: [],
            maxLogFile: "",
            maxLogTime: "",
            heartbeatSeconds: "",
        },
    });
    const { control, handleSubmit, reset, formState: { errors, isSubmitting }, } = methods;
    // Reset form when AP data is loaded
    useEffect(() => {
        var _a, _b, _c, _d;
        if (ap) {
            // Format lastUpdated date for display
            const formatDate = (dateString) => {
                if (!dateString)
                    return "";
                const date = new Date(dateString.endsWith("Z") ? dateString : dateString + "Z");
                if (isNaN(date.getTime()))
                    return "";
                return date.toLocaleDateString(undefined, {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                });
            };
            reset({
                accessPointUID: ap.accessPointUID || "",
                accessPointName: ap.accessPointName || "",
                lastUpdated: formatDate(getLatestApActivityIso(ap)),
                calibrationDate: ap.calibrationDate
                    ? ap.calibrationDate.split("T")[0]
                    : "",
                installationDate: ap.installationDate
                    ? ap.installationDate.split("T")[0]
                    : "",
                toolTipText: ap.toolTipText || "",
                isActive: (_a = ap.isActive) !== null && _a !== void 0 ? _a : true,
                address: ap.address || "",
                place: ap.place || "",
                administratorName: ap.administratorName || "",
                administratorPhone: ap.administratorPhone || "",
                administrators: ap.administrators || [],
                maxLogFile: String((_b = ap.configuration) === null || _b === void 0 ? void 0 : _b.maxLogFile),
                maxLogTime: String((_c = ap.configuration) === null || _c === void 0 ? void 0 : _c.maxLogTime),
                heartbeatSeconds: String((_d = ap.configuration) === null || _d === void 0 ? void 0 : _d.heartbeatSeconds),
            });
        }
    }, [ap, reset]);
    // Validation rules
    const validationRules = {
        accessPointUID: {
            required: t("validation:required", { field: "Unique ID" }),
        },
        accessPointName: {
            required: t("validation:required", { field: "Location Name" }),
        },
        lastUpdated: {},
        calibrationDate: {},
        installationDate: {},
        toolTipText: {},
        address: {},
        place: {},
        // Optional fields: empty values are skipped by react-hook-form, so these only
        // fire once the user has typed something.
        administratorName: {
            pattern: {
                // European names: letters (incl. accented), spaces, hyphens, apostrophes, dots.
                value: /^[A-Za-zÀ-ÖØ-öø-ÿĀ-ž][A-Za-zÀ-ÖØ-öø-ÿĀ-ž\s'’.-]*$/,
                message: t("validation:nameLetters"),
            },
            minLength: {
                value: 2,
                message: t("validation:minLength", {
                    field: t("common:labels.administratorName"),
                    min: 2,
                }),
            },
            maxLength: {
                value: 100,
                message: t("validation:maxLength", {
                    field: t("common:labels.administratorName"),
                    max: 100,
                }),
            },
        },
        administratorPhone: {
            pattern: {
                // Digits plus the separators used in European numbers – no letters.
                value: /^\+?[0-9][0-9\s\-().]*$/,
                message: t("validation:phoneDigits"),
            },
            validate: (value) => {
                const digits = (value || "").replace(/\D/g, "");
                if (!digits)
                    return true;
                return (digits.length >= 7 && digits.length <= 15) || t("validation:phone");
            },
        },
        administrators: {},
    };
    // Converts a Date object or "YYYY-MM-DD" string to UTC midnight ISO string using
    // local date components so the calendar day the user picked is preserved.
    const toUtcMidnight = (value) => {
        if (!value)
            return undefined;
        const d = value instanceof Date ? value : new Date(value);
        if (isNaN(d.getTime()))
            return undefined;
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}T00:00:00.000Z`;
    };
    // Form submission handler for general settings
    const handleFormSubmit = (data) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        if (!(ap === null || ap === void 0 ? void 0 : ap.accessPointUID)) {
            toast.error(t("common:toasts.accessPointUidRequired"));
            return;
        }
        const parseConfigNum = (val, fallback) => {
            const n = Number(val);
            return val !== "" && val !== "undefined" && !isNaN(n) ? n : fallback;
        };
        const updateCommand = {
            accessPointUID: ap.accessPointUID,
            accessPointName: data.accessPointName,
            calibrationDate: toUtcMidnight(data.calibrationDate),
            installationDate: toUtcMidnight(data.installationDate),
            toolTipText: data.toolTipText,
            isActive: data.isActive,
            address: data.address,
            place: data.place,
            administratorName: data.administratorName,
            administratorPhone: data.administratorPhone,
            administrators: data.administrators,
            isGeneralSettings: true,
            configuration: Object.assign(Object.assign({}, ap.configuration), { maxLogFile: parseConfigNum(data.maxLogFile, (_a = ap.configuration) === null || _a === void 0 ? void 0 : _a.maxLogFile), maxLogTime: parseConfigNum(data.maxLogTime, (_b = ap.configuration) === null || _b === void 0 ? void 0 : _b.maxLogTime), heartbeatSeconds: parseConfigNum(data.heartbeatSeconds, (_c = ap.configuration) === null || _c === void 0 ? void 0 : _c.heartbeatSeconds) }),
        };
        try {
            const result = yield dispatch(updateAccessPointGeneral({
                accessPointUid: ap.accessPointUID,
                data: updateCommand,
            })).unwrap();
            toast.success(t("common:toasts.accessPointUpdatedSuccessfully"));
        }
        catch (error) {
            toast.error(error || t("common:toasts.failedToUpdateAccessPoint"));
        }
    });
    return {
        methods,
        control,
        errors,
        handleSubmit,
        validationRules,
        handleFormSubmit,
        isSubmitting: updateGeneralLoading || updateConfigLoading,
    };
};
